// components/profile/ProfileButton.tsx
'use client';

import { User } from 'lucide-react';
import type { Session } from 'next-auth';
import Image from 'next/image';
import Link from 'next/link';

import { useUnreadCount } from '../../hooks/useUnreadCount';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

interface Props {
  className?: string;
  onClickSignIn?: () => void;
  onPrefetchSignIn?: () => void; // ✅ NEW: prefetch auth chunks on hover/focus/touch
  session: Session | null;
  isAuthenticated?: boolean;
}

export default function ProfileButton({
  className,
  onClickSignIn,
  onPrefetchSignIn,
  session,
  isAuthenticated = Boolean(session),
}: Props) {
  const { count: unreadCount, mode } = useUnreadCount({ enabled: isAuthenticated });

  if (!session) {
    return (
      <div
        className={className}
        // ✅ если пользователь наведёт мышь на область кнопки — заранее подкачаем чанки
        onPointerEnter={onPrefetchSignIn}
        onFocus={onPrefetchSignIn}
        onTouchStart={onPrefetchSignIn}
      >
        <Button
          onClick={onClickSignIn}
          variant="default"
          className="flex items-center gap-2"
          // ✅ prefetch прямо на кнопку тоже (на всякий случай)
          onPointerEnter={onPrefetchSignIn}
          onMouseEnter={onPrefetchSignIn}
          onFocus={onPrefetchSignIn}
          onTouchStart={onPrefetchSignIn}
        >
          <User size={16} />
          Войти
        </Button>
      </div>
    );
  }

  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div className={className}>
      <Button asChild variant="ghost" className="flex items-center gap-2 p-1 h-auto rounded-full">
        <Link href="/profile" aria-label="Профиль">
          <div className="relative">
            {session.user?.image ? (
              <Image
                src={session.user.image}
                alt="Аватар пользователя"
                width={32}
                height={32}
                className="rounded-full"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                <User size={18} />
              </div>
            )}

            {/* ✅ Индикатор режима обновлений */}
            <span
              aria-hidden="true"
              title={mode === 'sse' ? 'Live (SSE)' : mode === 'poll' ? 'Fallback (poll)' : 'Off'}
              className={cn(
                'absolute -bottom-0.5 -left-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white/70 dark:ring-slate-900/40',
                mode === 'sse'
                  ? 'bg-emerald-400'
                  : mode === 'poll'
                    ? 'bg-slate-300'
                    : 'bg-slate-500',
              )}
            />

            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center">
                <span className="animate-ping absolute inline-flex h-4 w-4 rounded-full bg-sky-400 opacity-75" />
                <span
                  className="relative inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-sky-500 text-[10px] font-semibold leading-none text-white tabular-nums"
                  aria-label={`Новых уведомлений: ${unreadCount}`}
                >
                  {badgeText}
                </span>
              </span>
            )}
          </div>

          <span className="hidden sm:inline text-slate-200">{session.user?.name || 'Профиль'}</span>
        </Link>
      </Button>
    </div>
  );
}
